import { useCallback } from "react";
import { warningToaster } from "utils/helpers/common/alert-service";

const useUtils = () => {
  const isCurrentWidthMinimum = useCallback((): boolean => {
    return window.innerWidth <= 1279;
  }, []);

  const getValidFilters = useCallback((filters: any) => {
    const queryParams: any = {};
    for (let key in filters) {
      const param = filters[key];
      if (param) {
        queryParams[key] = param;
      }
    }
    return queryParams;
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getLocalFilePath = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  const getQueryParams = useCallback((): any => {
    return window.location.search
      .replace(/^\?/, "")
      .split("&")
      .reduce((obj, str) => {
        if (!str) return obj;
        const pair = str.split("=");
        const key = decodeURIComponent(pair[0] || "");
        const value = decodeURIComponent(pair.slice(1).join("=") || "");
        return { ...obj, [key]: value };
      }, {});
  }, []);

  const setCSSVariable = useCallback((variableName: string, value: any) => {
    document.documentElement.style.setProperty(variableName, value);
  }, []);

  const removeHyphensFromCnic = useCallback((cnic: string): string => {
    return cnic.replace(/-/g, "");
  }, []);

  const addHyphensToCnic = useCallback((cnic: string): string => {
    const str = removeHyphensFromCnic(cnic);
    if (str.length !== 13 || /[a-zA-Z]/.test(str)) {
      warningToaster("CNIC is invalid");
      return "";
    } else {
      const part1 = str.slice(0, 5);
      const part2 = str.slice(5, 12);
      const part3 = str.slice(12);
      return `${part1}-${part2}-${part3}`;
    }
  }, [removeHyphensFromCnic]);

  const getFileType = useCallback((file: File) => {
    return file.type;
  }, []);

  const getTodayDate = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // getMonth() is zero-based
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const isFileOfType = useCallback((file: File, types: string[] | string) => {
    const fileTypes = typeof types === "string" ? [types] : [...types];
    return fileTypes.includes(getFileType(file));
  }, [getFileType]);

  const downloadFileWithUrl = useCallback((url: string) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    let urlToDownload = "";
    if (baseUrl && url.includes(baseUrl)) {
      urlToDownload = url;
    } else {
      urlToDownload = `${baseUrl}/${url}`;
    }

    const a = document.createElement("a");
    a.href = urlToDownload;
    a.download = urlToDownload;
    a.target = "_blank";
    a.click();
  }, []);

  const downloadFileWithUrl2 = useCallback((url: string) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    let urlToDownload = "";

    if (baseUrl && url.includes(baseUrl)) {
      urlToDownload = url;
    } else {
      urlToDownload = `${baseUrl}/${url}`;
    }

    urlToDownload = urlToDownload.replace(/\/storage\//, "//storage//");

    window.open(urlToDownload, "_blank", "noopener,noreferrer");
  }, []);

  const extractAfterZeroDot = useCallback((str: string, fieldIndex: number = 0) => {
    const prefix = `${fieldIndex}.`;
    const index = str.indexOf(prefix);

    if (index !== -1) {
      return str.substring(index + prefix.length);
    }

    return "";
  }, []);

  const openExternalLink = useCallback((url: string) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    let urlToNavigate = "";
    if (baseUrl && url.includes(baseUrl)) {
      urlToNavigate = url;
    } else {
      urlToNavigate = `${baseUrl}/${url}`;
    }
    window.open(urlToNavigate, "_blank");
  }, []);

  const getYears = useCallback((
    from: number,
    to?: number  // Make optional without default
  ): number[] => {
    const currentYear = new Date().getFullYear();
    const maxYear = to || Math.max(currentYear, /* you could add buffer */ currentYear + 1);
    const years = Array.from({ length: maxYear - from + 1 }, (_, i) => from + i);
    return years;
  }, []);

  const hasAccess = useCallback((permission: string): boolean => {
    const permissions: any = localStorage.getItem("permissions") || [];
    return permissions.includes(permission);
  }, []);

  const getDateFromDateTime = useCallback((dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const compareDateStrings = useCallback((date1: string, date2: string): boolean => {
    const startDate = new Date(date1);
    const endDate = new Date(date2);
    return startDate < endDate;
  }, []);

  const createQuery = useCallback((params: any = {}): string => {
    let query = "";
    let i = 0;
    for (let key in params) {
      query += `${i === 0 ? "?" : "&"}${key}=${params[key]}`;
      i++;
    }

    return query;
  }, []);

  const handleSearchChange = useCallback((
    event: any,
    setSearch: Function,
    per_page: number | string = 10,
    requestCallBack: Function,
    filters: any = {}
  ) => {
    const { value } = event.target;
    setSearch(value);
    if (value === "") requestCallBack(1, value, per_page, filters);
  }, []);

  const handleTableSearch = useCallback((
    event: any,
    per_page: number | string = 10,
    requestCallBack: Function,
    filters: any = {}
  ) => {
    if (event.key === "Enter") {
      requestCallBack(1, event.target.value, per_page, filters);
    }
  }, []);

  const splitPathBySlash = useCallback((path: string): string[] => {
    return path.split("/");
  }, []);

  const getPathToSetRoute = useCallback((
    path: string,
    isModule: boolean = false
  ): string => {
    const splittedPath: string[] = splitPathBySlash(path);
    const pagePath: string = splittedPath[splittedPath.length - 1];
    let pathToReturn: string = pagePath;
    if (isModule) {
      const modulePath: string = splittedPath[splittedPath.length - 2];
      pathToReturn = `${modulePath}/${pagePath}`;
    }
    return pathToReturn;
  }, [splitPathBySlash]);

  const concatPathWithBackendUrl = useCallback((path: string): string => {
    return process.env.REACT_APP_BASE_URL + path;
  }, []);

  const getFileExtensionByUrl = useCallback((url: string) => {
    return url.split(".").pop();
  }, []);

  const isFileOfExcelType = useCallback((file: File) => {
    const extensions: string[] = [
      "text/csv",
      "text/xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    return extensions.includes(getFileType(file));
  }, [getFileType]);

  return {
    getTodayDate,
    handleTableSearch,
    isFileOfExcelType,
    getFileExtensionByUrl,
    getPathToSetRoute,
    concatPathWithBackendUrl,
    splitPathBySlash,
    isCurrentWidthMinimum,
    handleSearchChange,
    scrollToTop,
    getYears,
    extractAfterZeroDot,
    getLocalFilePath,
    getQueryParams,
    setCSSVariable,
    downloadFileWithUrl,
    downloadFileWithUrl2,
    getFileType,
    isFileOfType,
    hasAccess,
    getDateFromDateTime,
    compareDateStrings,
    openExternalLink,
    createQuery,
    addHyphensToCnic,
    removeHyphensFromCnic,
    getValidFilters,
  };
};

export default useUtils;
